import type {
  BookingTimelineNode,
  BookingTimelineStatus,
} from "@interfaces/myQuotesOrBooking.types";

const KYC_TOTAL_FILE_SIZE_ERROR =
  "One or more files exceed the size limit of 5MB. Please upload smaller files.";

const normalizeKycUploadError = (errorMessage: string) => {
  if (/JSON/i.test(errorMessage)) {
    return KYC_TOTAL_FILE_SIZE_ERROR;
  }

  return errorMessage;
};

const TIMELINE_STATUSES = new Set<BookingTimelineStatus>([
  "done",
  "inprogress",
  "pending",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getTimelineItems = (payload: unknown): unknown[] | null => {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return null;
  if (Array.isArray(payload.timeline)) return payload.timeline;
  if (Array.isArray(payload.data)) return payload.data;
  if (isRecord(payload.data) && Array.isArray(payload.data.timeline)) {
    return payload.data.timeline;
  }
  return null;
};

const normalizeTimeline = (items: unknown[]): BookingTimelineNode[] =>
  items.flatMap((item) => {
    if (!isRecord(item)) return [];

    const normalizedStatus =
      typeof item.status === "string"
        ? item.status.trim().toLowerCase().replace(/[\s_-]/g, "")
        : "pending";
    const status = TIMELINE_STATUSES.has(
      normalizedStatus as BookingTimelineStatus
    )
      ? (normalizedStatus as BookingTimelineStatus)
      : "pending";

    return [
      {
        title: typeof item.title === "string" ? item.title : "",
        subheading:
          typeof item.subheading === "string" ? item.subheading : "",
        status,
        childs: normalizeTimeline(
          Array.isArray(item.childs) ? item.childs : []
        ),
      },
    ];
  });

export const getOrderTimeline = async (
  orderEkey: string,
  token: string
): Promise<BookingTimelineNode[]> => {
  if (!orderEkey || !token) {
    throw new Error("Timeline is unavailable for this booking.");
  }

  const query = new URLSearchParams({
    operation: "getOrderTimeline",
    quote_ekey: orderEkey,
    token,
  });
  const response = await fetch(`/api/oms-service?${query.toString()}`, {
    method: "GET",
    cache: "no-store",
  });
  const result = await response.json().catch(() => null);

  if (!response.ok || !isRecord(result) || result.success !== true) {
    const error =
      isRecord(result) && typeof result.error === "string"
        ? result.error
        : "Failed to load booking timeline";
    throw new Error(error);
  }

  const items = getTimelineItems(result.data);
  if (!items) throw new Error("Invalid booking timeline response");

  return normalizeTimeline(items);
};

export const createOMSQuote = async (
  payload: any,
  token: any
): Promise<any> => {
  try {
    if (!token) {
      return { success: false, data: null };
    }

    const response = await fetch("/api/oms-service", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        operation: "createQuote",
        payload,
        token,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("[OMS Service] Error creating quote:", error.message);
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * Update OMS Quote via Next.js API route (server-side)
 */
export const updateOMSQuote = async (
  payload: any,
  quote_ekey: string,
  token: any
): Promise<any> => {
  try {
    if (!token) {
      return { success: false, data: null };
    }

    const response = await fetch("/api/oms-service", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        operation: "updateQuote",
        payload,
        quote_ekey,
        token,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("[OMS Service] Error updating quote:", error.message);
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * Get OMS Quote via Next.js API route (server-side)
 */
export const getOMSQuote = async (
  quote_ekey: string,
  token: any
): Promise<any> => {
  try {
    if (!token) {
      return { success: false, data: null };
    }

    const response = await fetch(
      `/api/oms-service?quote_ekey=${encodeURIComponent(quote_ekey)}&token=${encodeURIComponent(token)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("[OMS Service] Error fetching quote:", error.message);
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * Create a new OMS quote from a legacy booking ekey via Next.js API route.
 */
export const createNewBookingFromOldEkey = async (
  oldEkey: string,
  token: string
): Promise<any> => {
  try {
    if (!token || !oldEkey) {
      return { success: false, data: null };
    }

    const response = await fetch("/api/oms-service", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        operation: "newBooking",
        oldEkey,
        token,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("[OMS Service] Error creating new booking:", error.message);
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * Create Booking Order via Next.js API route (server-side)
 */
export const createBookingOrder = async (
  payload: any,
  token: string,
  isInApp?: boolean
): Promise<any> => {
  try {
    const response = await fetch("/api/oms-service", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        operation: "createBooking",
        payload,
        token,
        isInApp,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("[OMS Service] Error creating booking:", error.message);
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * Get Old Order Details via Next.js API route (client-side)
 */
export const getOldOrderDetails = async (
  quote_ekey: string,
  token: string,
  isPaymentFlow?: boolean,
): Promise<any> => {
  try {
    if (!token || !quote_ekey) {
      return { success: false, data: null };
    }
    
    let query = `?operation=getOldOrderDetails&quote_ekey=${encodeURIComponent(quote_ekey)}&token=${encodeURIComponent(token)}`;
    if (isPaymentFlow) {
      query += '&is_payment_flow=true';
    }

    const response = await fetch(
      `/api/oms-service${query}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.error("[OMS Service] HTTP error:", response.status);
      return {
        success: false,
        data: null,
      };
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("[OMS Service] Error getting old order details:", error.message);
    return {
      success: false,
      data: null,
    };
  }
};

export const uploadKycDocuments = async (
  token: string | null,
  ekey: string,
  payload: {
    pancard?: string;
    adhaar_front?: string;
    adhaar_back?: string;
  },
): Promise<{ success: boolean; error: string; data?: any }> => {
  try {
    if (!token) {
      return { success: false, error: "Token is required" };
    }

    if (!ekey) {
      return { success: false, error: "ekey is required" };
    }

    const response = await fetch("/api/oms-service", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-upload-type": "kyc",
        "retailtoken": token,
        "ekey": ekey,
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    let data: any = null;

    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch {
      data = responseText;
    }

    const htmlTitle = typeof data === "string" ? data.match(/<title>(.*?)<\/title>/i)?.[1] : "";
    const upstreamError =
      data?.error ||
      data?.message ||
      data?.data?.message ||
      htmlTitle ||
      (typeof data === "string" ? data : "");

    if (!response.ok || !data?.success) {
      const normalizedError = normalizeKycUploadError(
        upstreamError || `HTTP ${response.status}`
      );

      return { 
        success: false, 
        error: normalizedError,
        data: data?.data,
      };
    }

    return { success: true, error: "", data: data?.data };
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    const normalizedError = normalizeKycUploadError(errorMessage);
    console.error("[OMS Service] Error uploading KYC documents:", errorMessage);
    return { success: false, error: normalizedError };
  }
};
